import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles, Check, FileText } from 'lucide-react';

const AIBookAssistant = ({ boekNaam, skrywerNaam, genre, previewText, bookFile, onBlurbGenerated, onCoverSelect, initialBlurb }) => {
  const [generatingBlurb, setGeneratingBlurb] = useState(false);
  const [generatingCovers, setGeneratingCovers] = useState(false);
  const [blurb, setBlurb] = useState(initialBlurb || '');
  const [covers, setCovers] = useState([]);
  const [selectedCover, setSelectedCover] = useState(null);
  const [blurbGenerated, setBlurbGenerated] = useState(!!initialBlurb);

  const generateBlurb = async (bookFile) => {
    if (!bookFile) {
      toast.error('Geen boek lêer gevind nie. Laai asseblief eers die boek op.');
      return;
    }

    setGeneratingBlurb(true);
    try {
      const formData = new FormData();
      formData.append('book_file', bookFile);
      formData.append('boek_naam', boekNaam || 'Untitled Book');
      formData.append('genre', genre || 'Fiksie');

      toast.info('AI lees die HELE boek en skep akkurate blurb... Dit kan 30-60 sekondes neem ⏳', { duration: 8000 });

      const response = await axios.post(`${API}/generate-ai-blurb`, formData);

      setBlurb(response.data.blurb);
      setBlurbGenerated(true);
      onBlurbGenerated(response.data.blurb);
      toast.success('Professionele blurb gegenereer! Jy kan dit redigeer indien nodig.');
    } catch (error) {
      console.error('AI blurb generation error:', error);
      toast.error('Kon nie AI blurb genereer nie. Probeer weer.');
    } finally {
      setGeneratingBlurb(false);
    }
  };

  const handleBlurbEdit = (e) => {
    const newBlurb = e.target.value;
    setBlurb(newBlurb);
    onBlurbGenerated(newBlurb);
  };

  const generateCovers = async () => {
    if (!previewText || previewText.trim().length < 100) {
      toast.error('Boek voorskou is te kort. Laai asseblief eers die boek lêer op.');
      return;
    }

    setGeneratingCovers(true);
    setCovers([]);
    setSelectedCover(null);

    try {
      const formData = new FormData();
      formData.append('preview_text', previewText);
      formData.append('boek_naam', boekNaam || 'Untitled Book');
      formData.append('skrywer_naam', skrywerNaam || 'Anonymous');
      formData.append('genre', genre || 'Fiksie');
      
      // Use blurb if available (better context for covers)
      if (blurb && blurb.length > 50) {
        formData.append('blurb', blurb);
        toast.info('AI gebruik blurb om akkurate voorblaaie te skep... ⏳', { duration: 5000 });
      } else {
        toast.info('AI analiseer boek en skep voorblaaie... Dit kan tot 90 sekondes neem ⏳', { duration: 5000 });
      }

      const response = await axios.post(`${API}/generate-ai-covers`, formData);

      setCovers(response.data.covers);
      toast.success(response.data.message + ' Kies een!');
    } catch (error) {
      console.error('AI cover generation error:', error);
      toast.error('Kon nie AI voorblaaie genereer nie. Probeer weer.');
      // Ensure covers are cleared even on error so button stays enabled
      setCovers([]);
    } finally {
      // Always set to false to ensure button re-enables
      setGeneratingCovers(false);
    }
  };

  const handleSelectCover = (cover) => {
    setSelectedCover(cover.id);
    
    // Convert base64 to blob and create file
    const byteString = atob(cover.image_base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: 'image/png' });
    const file = new File([blob], `ai_cover_${Date.now()}.png`, { type: 'image/png' });
    
    onCoverSelect(file, `data:image/png;base64,${cover.image_base64}`);
    toast.success('Voorblad gekies! ✓');
  };

  return (
    <div className="space-y-6">
      {/* AI Blurb Generator */}
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <FileText className="h-5 w-5" />
            Stap 1: AI Blurb Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-700">
            AI sal 'n professionele, wêreldklas blurb skep gebaseer op jou boek se inhoud.
          </p>
          
          <Button
            onClick={() => generateBlurb(bookFile)}
            disabled={generatingBlurb || !bookFile || blurbGenerated}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            {generatingBlurb ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Genereer Blurb...
              </>
            ) : blurbGenerated ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Blurb Gegenereer
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Genereer Professionele Blurb
              </>
            )}
          </Button>

          {blurb && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Gegenereerde Blurb (Jy kan dit redigeer):
              </label>
              <Textarea
                value={blurb}
                onChange={handleBlurbEdit}
                rows={6}
                className="w-full"
                placeholder="AI blurb sal hier verskyn..."
              />
              <p className="text-xs text-gray-600">
                ✓ {blurb.split(' ').length} woorde | Professioneel gegenereer
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Cover Generator */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Sparkles className="h-5 w-5" />
            Stap 2: AI Voorblad Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-700">
            AI sal 3 professionele voorblaaie skep gebaseer op die storie se inhoud.
          </p>

          <Button
            onClick={generateCovers}
            disabled={generatingCovers || (!previewText && !blurb)}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
          >
            {generatingCovers ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Genereer Voorblaaie...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {covers.length > 0 ? 'Genereer Nuwe Voorblaaie' : 'Genereer AI Voorblaaie'}
              </>
            )}
          </Button>

          {generatingCovers && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-gray-600">AI is besig om voorblaaie te skep...</p>
              <p className="text-sm text-gray-500 mt-2">Dit kan tot 1 minuut neem ⏳</p>
            </div>
          )}

          {covers.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Kies 'n voorblad:</p>
                <p className="text-xs text-gray-500 italic">Nie tevrede? Klik weer op "Genereer Nuwe Voorblaaie"</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {covers.map((cover) => (
                  <Card
                    key={cover.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      selectedCover === cover.id
                        ? 'ring-4 ring-green-500 shadow-xl'
                        : 'hover:ring-2 hover:ring-blue-300'
                    }`}
                    onClick={() => handleSelectCover(cover)}
                  >
                    <CardContent className="p-3">
                      <div className="relative">
                        <img
                          src={`data:image/png;base64,${cover.image_base64}`}
                          alt={`AI Cover ${cover.id + 1}`}
                          className="w-full h-auto rounded shadow-md"
                        />
                        {selectedCover === cover.id && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
                            <Check className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <p className="text-center text-sm font-medium text-gray-700 mt-2">
                        Opsie {cover.id + 1}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {covers.length === 0 && !generatingCovers && (
            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Sparkles className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">
                Klik op "Genereer AI Voorblaaie" om te begin
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIBookAssistant;
